import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { useApiFormError } from '../../../library/useApiFormError';
import { toDateInputValue } from '../../../library/dateTimeHelper';
import { downscaleImage } from '../../../library/imageHelper';
import { EmitInvoiceRequest, IInvoice } from '../../../library/models/invoice';
import { IPatientPurchase } from '../../../library/models/patientPurchase';
import { formatPrice } from '../../../library/purchaseDisplay';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { AppDatePicker } from '../../pickers/AppDatePicker';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import {
    dialogActionsStyles,
    dialogFormStyles,
    dialogTitleStyles,
    fieldGridStyles,
    formAlertStyles,
    fullWidthFieldStyles,
    photoHintStyles,
    photoPreviewStyles,
    photoRowStyles,
    sectionHintStyles,
    sectionTitleStyles,
    summaryBoxStyles,
    summaryMetaStyles,
    summaryServiceStyles,
} from './emitInvoiceDialog.styles';
import { EmitInvoiceFormValues, createEmitInvoiceSchema } from './emitInvoiceSchema';
import { localised } from '../../../library/models/localisedText';

interface EmitInvoiceDialogProps {
    open: boolean;
    purchase: IPatientPurchase;
    /** Prefills the client, which is usually but not always who the invoice is for. */
    patientName: string;
    patientEmail?: string;
    onClose: () => void;
    /** The patient's invoice list has to pick the new one up. */
    onEmitted: () => void;
}

/**
 * Emits a fiscal invoice for a sale.
 *
 * Three things about this dialog are deliberate.
 *
 * **The photograph is required and is captured, not chosen.** `capture="environment"` opens
 * the rear camera straight from a phone, which is where this is actually used -- a therapist
 * at the desk with the patient's till receipt in their hand. The picture is shrunk in the
 * background once it is taken, because a 4MB photograph of a strip of thermal paper is grain
 * -- but nothing waits on that, so a slow or impossible decode costs a bigger upload rather
 * than a button that will not enable.
 *
 * **The receipt's amount is asked for rather than assumed.** It defaults to what the purchase
 * cost, and it is editable, because a receipt covering two purchases or rounded at the till is
 * a real thing that happens -- and a document claiming a figure the paper does not show is
 * worse than one that admits the difference.
 *
 * **Nothing here says what the invoice is worth.** The amount comes from the purchase,
 * server-side, the same rule `AddPurchaseDialog` follows for the price of a sale.
 *
 * A dialog, so react-hook-form and Yup rather than plain controlled state -- the split by
 * shape this app keeps. The photograph is the exception and lives in its own state: a `File`
 * is not a form value, and Yup has nothing useful to say about one.
 */
export const EmitInvoiceDialog = (props: EmitInvoiceDialogProps): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { user } = useAuth();
    const isCompact = useIsCompact();

    const schema = useMemo(() => createEmitInvoiceSchema(translate), [translate]);

    const defaultValues = useMemo(
        (): EmitInvoiceFormValues => ({
            clientName: props.patientName,
            clientCif: '',
            clientAddress: '',
            clientCity: '',
            clientState: '',
            clientEmail: props.patientEmail ?? '',
            receiptNumber: '',
            receiptDate: toDateInputValue(new Date()),
            receiptAmount: (props.purchase.price ?? 0).toFixed(2),
        }),
        [props.patientName, props.patientEmail, props.purchase.price]
    );

    const {
        register,
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<EmitInvoiceFormValues>(schema, { defaultValues });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, show, clear } = useApiFormError<EmitInvoiceFormValues>(
        setError, knownFields, translate, ResourcesIds.EMIT_INVOICE_ERROR);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);

    const fileInput = useRef<HTMLInputElement>(null);

    const emitInvoice = useReactMutation(services.invoicing.emitInvoiceMutation());

    // Cleared on every open, so the last invoice's client is never applied to the next one.
    useEffect(() => {
        if (props.open) {
            reset(defaultValues);
            clear();
            setPhoto(null);
            setPhotoPreview(null);
        }
    }, [props.open, reset, defaultValues, clear]);

    // The preview is an object URL, so it has to be handed back or every retake leaks a
    // few hundred kilobytes for the life of the tab.
    useEffect(
        () => () => {
            if (photoPreview) {
                URL.revokeObjectURL(photoPreview);
            }
        },
        [photoPreview]
    );

    /**
     * Takes the photograph now and shrinks it afterwards.
     *
     * The order matters. Downscaling has to decode the image, and a decode can be slow on an
     * old phone or fail outright on a format the browser will not read -- so waiting for it
     * before accepting the file would leave the therapist looking at a dead button for
     * reasons that have nothing to do with what they photographed. The full-size file is
     * valid on its own, the server accepts up to 5MB, and the smaller version simply replaces
     * it if and when it arrives.
     */
    const onPickPhoto = (event: ChangeEvent<HTMLInputElement>): void => {
        const chosen: File | undefined = event.target.files?.[0];

        // Cleared straight away so choosing the same file twice still fires a change event.
        event.target.value = '';

        if (!chosen) {
            return;
        }

        setPhoto(chosen);
        setPhotoPreview((previous: string | null) => {
            if (previous) {
                URL.revokeObjectURL(previous);
            }

            return URL.createObjectURL(chosen);
        });

        // Never rejects: a browser that cannot re-encode hands the original back. See
        // `downscaleImage`. Nothing waits on this, so a decode that never finishes costs a
        // larger upload rather than a dialog that cannot be submitted.
        void downscaleImage(chosen).then((prepared: File) => {
            if (prepared !== chosen) {
                setPhoto(prepared);
            }
        });
    };

    const toRequest = (values: EmitInvoiceFormValues): EmitInvoiceRequest => ({
        purchaseId: props.purchase.id,
        client: {
            name: values.clientName.trim(),
            cif: values.clientCif.trim() || undefined,
            address: values.clientAddress.trim() || undefined,
            city: values.clientCity.trim() || undefined,
            state: values.clientState.trim() || undefined,
            email: values.clientEmail.trim() || undefined,
        },
        receipt: {
            number: values.receiptNumber.trim(),
            date: values.receiptDate,
            amount: Number(values.receiptAmount),
        },
    });

    const onValid = (values: EmitInvoiceFormValues): void => {
        clear();

        // The photograph is a File and deliberately outside react-hook-form, so the button
        // and this guard are what carry it rather than the schema.
        if (!photo) {
            show(translate(ResourcesIds.EMIT_INVOICE_RECEIPT_PHOTO_REQUIRED));

            return;
        }

        emitInvoice.mutate(
            { request: toRequest(values), file: photo },
            {
                onSuccess: (invoice: IInvoice) => {
                    showSnackbar(
                        translate(ResourcesIds.EMIT_INVOICE_SUCCESS, {
                            document: invoice.documentNumber ?? '',
                        }),
                        'success'
                    );
                    props.onEmitted();
                    props.onClose();
                },
                // The provider's own words where there are any: "seria FCT nu exista" is
                // something the therapist can act on.
                onError: report,
            }
        );
    };

    const isBusy: boolean = isSubmitting || emitInvoice.isPending;

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isCompact}
        >
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.EMIT_INVOICE_TITLE)}
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && (
                        <Alert severity="error" sx={formAlertStyles}>
                            {formError}
                        </Alert>
                    )}

                    {/* Read-only: what is being invoiced is the sale, and the amount is the
                        server's to decide. */}
                    <Box sx={summaryBoxStyles}>
                        <Typography variant="body1" sx={summaryServiceStyles}>
                            {localised(props.purchase.serviceNameRo, props.purchase.serviceNameEn, i18n.language)}
                        </Typography>
                        <Typography variant="body2" sx={summaryMetaStyles}>
                            {/* Never null in practice -- the dialog is only reachable from a
                                staff view, and the API leaves money out only for a patient
                                looking at their own purchase. */}
                            {formatPrice(props.purchase.price ?? 0)}
                            {' · '}
                            {translate(ResourcesIds.EMIT_INVOICE_SOLD_ON, {
                                date: props.purchase.assignedOn,
                            })}
                        </Typography>
                    </Box>

                    <Typography variant="subtitle1" sx={sectionTitleStyles}>
                        {translate(ResourcesIds.EMIT_INVOICE_CLIENT_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={sectionHintStyles}>
                        {translate(ResourcesIds.EMIT_INVOICE_CLIENT_DESCRIPTION)}
                    </Typography>

                    <Box sx={fieldGridStyles}>
                        <TextField
                            {...register('clientName')}
                            fullWidth
                            required
                            sx={fullWidthFieldStyles}
                            label={translate(ResourcesIds.EMIT_INVOICE_CLIENT_NAME_LABEL)}
                            {...fieldProps(errors, 'clientName')}
                        />
                        <TextField
                            {...register('clientCif')}
                            fullWidth
                            label={translate(ResourcesIds.EMIT_INVOICE_CLIENT_CIF_LABEL)}
                            {...fieldProps(errors, 'clientCif')}
                        />
                        <TextField
                            {...register('clientEmail')}
                            fullWidth
                            label={translate(ResourcesIds.EMIT_INVOICE_CLIENT_EMAIL_LABEL)}
                            {...fieldProps(errors, 'clientEmail')}
                        />
                        <TextField
                            {...register('clientAddress')}
                            fullWidth
                            sx={fullWidthFieldStyles}
                            label={translate(ResourcesIds.EMIT_INVOICE_CLIENT_ADDRESS_LABEL)}
                            {...fieldProps(errors, 'clientAddress')}
                        />
                        <TextField
                            {...register('clientCity')}
                            fullWidth
                            label={translate(ResourcesIds.EMIT_INVOICE_CLIENT_CITY_LABEL)}
                        />
                        <TextField
                            {...register('clientState')}
                            fullWidth
                            label={translate(ResourcesIds.EMIT_INVOICE_CLIENT_STATE_LABEL)}
                        />
                    </Box>

                    <Typography variant="subtitle1" sx={sectionTitleStyles}>
                        {translate(ResourcesIds.EMIT_INVOICE_RECEIPT_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={sectionHintStyles}>
                        {translate(ResourcesIds.EMIT_INVOICE_RECEIPT_DESCRIPTION)}
                    </Typography>

                    <Box sx={fieldGridStyles}>
                        <TextField
                            {...register('receiptNumber')}
                            fullWidth
                            required
                            label={translate(ResourcesIds.EMIT_INVOICE_RECEIPT_NUMBER_LABEL)}
                            {...fieldProps(errors, 'receiptNumber')}
                        />
                        <Controller
                            name="receiptDate"
                            control={control}
                            render={({ field }) => (
                                <AppDatePicker
                                    label={translate(ResourcesIds.EMIT_INVOICE_RECEIPT_DATE_LABEL)}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    inputRef={field.ref}
                                    fullWidth
                                    required
                                    // A receipt cannot be dated in the future. The API's own
                                    // one-month rule is a deadline on *emitting* keyed off the
                                    // sale date, not a bound on this field, and
                                    // `PurchaseInvoiceActions` already refuses to open the
                                    // dialog once it has passed -- so it is deliberately not
                                    // re-stated here as a date range.
                                    disableFuture
                                    {...fieldProps(errors, 'receiptDate')}
                                />
                            )}
                        />
                        <TextField
                            {...register('receiptAmount')}
                            fullWidth
                            required
                            label={translate(ResourcesIds.EMIT_INVOICE_RECEIPT_AMOUNT_LABEL)}
                            {...fieldProps(errors, 'receiptAmount')}
                        />
                    </Box>

                    <Box sx={photoRowStyles}>
                        {photoPreview && (
                            <Box
                                component="img"
                                src={photoPreview}
                                alt={translate(ResourcesIds.EMIT_INVOICE_RECEIPT_PHOTO_ALT)}
                                sx={photoPreviewStyles}
                            />
                        )}

                        <Button
                            variant="outlined"
                            onClick={() => fileInput.current?.click()}
                            disabled={isBusy}
                        >
                            {translate(
                                photo
                                    ? ResourcesIds.EMIT_INVOICE_RECEIPT_PHOTO_RETAKE
                                    : ResourcesIds.EMIT_INVOICE_RECEIPT_PHOTO
                            )}
                        </Button>

                        {!photo && (
                            <Typography variant="caption" sx={photoHintStyles}>
                                {translate(ResourcesIds.EMIT_INVOICE_RECEIPT_PHOTO_REQUIRED)}
                            </Typography>
                        )}

                        {/* `capture="environment"` opens the rear camera straight from a
                            phone, which is where this is used. A desktop browser ignores it
                            and shows the file picker, so nothing is lost by asking. */}
                        <input
                            ref={fileInput}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            hidden
                            onChange={onPickPhoto}
                            data-testid="receipt-photo-input"
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    <Button onClick={props.onClose} disabled={isBusy}>
                        {translate(ResourcesIds.EMIT_INVOICE_CANCEL)}
                    </Button>
                    <Button type="submit" variant="contained" disabled={!isValid || isBusy || !photo || !user}>
                        {translate(ResourcesIds.EMIT_INVOICE_SUBMIT)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
